/**
 * 데이터형 게임 카드 오케스트레이션 — 결정 #29. 저장된 GameCard를 런타임
 * GameModule(결정 #25)로 컴파일해 레지스트리에 등록/해제하고, 편집기·관리 UI가
 * 쓰는 저장/삭제/토글을 제공한다.
 *
 * 흐름:
 *  - 부팅(loadGameCards): 활성 카드를 모두 컴파일해 등록 → 새 대화 모듈 목록에 즉시 노출.
 *  - 저장(saveGameCard): 카드를 영속하고, 활성이면 재등록 / 비활성이면 해제.
 *  - 삭제(deleteGameCard): 카드를 소프트 삭제하고 레지스트리에서 해제.
 *
 * core 인터페이스(storage/레지스트리)만 다룬다(결정 #10). 컴파일은 core의
 * compileGameDefinition 순수 함수가 담당하므로 web은 등록 수명주기만 관리한다.
 */
import {
  compileGameDefinition,
  createGameCard,
  exportGameCardToRqt,
  exportGameCardToRqg,
  exportGameCardToRqp,
  gameDefinitionToRoqeetJson,
  getOfficialRqtSample,
  importGameCardFromJson,
  importGameCardFromRqp,
  importGameCardFromRqt,
  type LiveQuery,
  type GameCard,
  type OfficialRqtSample,
} from "@roqeet/core";
import { storage } from "./storage.js";
import { gameModules } from "./game-modules.js";
import { getDB } from "../platform/db/index.js";
import { toLiveQuery } from "../platform/db/live-query.js";
import { normalizeStoredGameCard } from "../platform/db/stored-game-card.js";

export type GameCardSourceExportState = "current" | "stale" | "legacy";

export interface StoredGameCardRecoveryState {
  invalidCount: number;
  repairedCount: number;
}

const MIB = 1024 * 1024;

export const GAME_CARD_IMPORT_SIZE_LIMIT_BYTES = {
  rqt: 5 * MIB,
  rqg: 5 * MIB,
  json: 5 * MIB,
  rqp: 20 * MIB,
} as const;
const ROQEET_DOWNLOAD_BLOB_TYPE = "application/octet-stream";
type GameCardImportKind = "rqt" | "rqg" | "json" | "rqp";

export function liveStoredGameCardRecoveryState(): LiveQuery<StoredGameCardRecoveryState> {
  return toLiveQuery(async () => {
    const rows = await getDB().gameCards.toArray();
    let invalidCount = 0;
    let repairedCount = 0;
    for (const row of rows) {
      const result = normalizeStoredGameCard(row);
      if (result.status === "invalid") invalidCount += 1;
      else if (result.repaired && !result.card.sync.deletedAt) repairedCount += 1;
    }
    return { invalidCount, repairedCount };
  });
}

/**
 * 저장된 모든 게임 카드를 컴파일해 레지스트리에 등록한다(부팅 시 1회, 결정 #29).
 * 활성 카드만 등록한다 — 비활성(초안/보관)은 저장돼 있으나 모듈 목록에 안 뜬다.
 * 컴파일 실패(손상된 정의)는 건너뛰고 계속한다(한 카드가 앱 부팅을 막지 않게).
 */
export async function loadGameCards(): Promise<void> {
  const cards = await storage.gameCards.list();
  for (const card of cards) {
    if (!card.enabled) continue;
    registerCard(card);
  }
}

/** 한 카드를 컴파일해 등록한다. 실패해도 throw하지 않고 콘솔에 남긴다. */
function registerCard(card: GameCard): boolean {
  try {
    gameModules.register(compileGameDefinition(card.definition));
    return true;
  } catch (e) {
    const id = typeof card.definition?.id === "string" ? card.definition.id : card.id;
    console.error(`게임 카드 컴파일 실패 (${id}):`, e);
    return false;
  }
}

/**
 * 게임 카드를 저장하고 레지스트리를 갱신한다(결정 #29). 활성이면 (재)등록,
 * 비활성이면 해제한다. 편집기의 "저장"이 이걸 호출한다.
 */
export async function saveGameCard(card: GameCard): Promise<void> {
  const module = card.enabled ? compileGameDefinition(card.definition) : undefined;
  await storage.gameCards.put(card);
  if (module) {
    gameModules.register(module);
  } else {
    gameModules.unregister(card.definition.id);
  }
}

/**
 * 게임 카드를 삭제(소프트)하고 레지스트리에서 해제한다(결정 #29/#20). 이미 이 모듈로
 * 시작한 기존 세션은 moduleId가 남지만, 모듈이 사라지면 전송 파이프라인이 자유대화
 * 경로로 폴백하므로 대화 자체는 깨지지 않는다(결정 #28 가산성).
 */
export async function deleteGameCard(card: GameCard): Promise<void> {
  await storage.gameCards.delete(card.id);
  gameModules.unregister(card.definition.id);
}

/**
 * 게임 카드를 단독 .rqg 런타임 문서로 내보내 파일로 다운로드한다(결정 #6/#29).
 * core가 휘발성 메타를 빼고 공유 가능한 정의만 직렬화한다 — 받는 쪽은 새 id로 가져온다.
 */
export function exportGameCard(card: GameCard): void {
  const json = exportGameCardToRqg(card);
  const blob = new Blob([json], { type: ROQEET_DOWNLOAD_BLOB_TYPE });
  downloadBlob(blob, `${safeDownloadName(card.definition.title)}.rqg`);
}

/**
 * 완성품 `.rqp` 패키지로 내보낸다. 루트 manifest.json + game.rqg를 ZIP으로 묶어,
 * 가이드의 설치 미리보기와 같은 패키지 검증 경로를 탄다.
 */
export function exportGameCardPackage(card: GameCard): void {
  const bytes = exportGameCardToRqp(card);
  const buffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  const blob = new Blob([buffer], { type: ROQEET_DOWNLOAD_BLOB_TYPE });
  downloadBlob(blob, `${safeDownloadName(card.definition.title)}.rqp`);
}

export function canExportGameCardSource(card: GameCard): boolean {
  return exportGameCardToRqt(card) !== undefined;
}

export function getGameCardSourceExportState(card: GameCard): GameCardSourceExportState | undefined {
  const source = card.extensions?.["sourceRqt"];
  if (typeof source !== "string" || !source.trim()) return undefined;
  const status = card.extensions?.["sourceRqtStatus"];
  if (canExportGameCardSource(card)) return "current";
  if (status === "stale" || status === "current") return "stale";
  return "legacy";
}

export function exportGameCardSource(card: GameCard): boolean {
  const source = exportGameCardToRqt(card);
  if (!source) return false;
  const blob = new Blob([source], { type: ROQEET_DOWNLOAD_BLOB_TYPE });
  downloadBlob(blob, `${safeDownloadName(card.definition.title)}.rqt`);
  return true;
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function safeDownloadName(title: string): string {
  const safe = title
    .normalize("NFKC")
    .replace(/[^\p{L}\p{N}._ -]+/gu, "_")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 60);
  return safe || "game";
}

/**
 * 파일에서 게임 카드를 가져와 저장하고 등록한다(결정 #6/#29). core가 id를 새로
 * 부여하고 definition.id를 재시드하므로 기존 카드/빌트인과 충돌하지 않는다. 저장된
 * 카드(활성)는 즉시 컴파일·등록되어 "새 대화" 모듈 목록에 노출된다. 가져온 카드 id 반환.
 */
export async function importGameCard(file: Blob): Promise<GameCard> {
  validateGameCardImportFileSize(file);
  const filename = gameCardImportFilename(file);
  const kind = gameCardImportKind(filename);
  const card = kind === "rqp"
    ? await importGameCardFromRqp(new Uint8Array(await file.arrayBuffer()))
    : kind === "rqt"
      ? importGameCardFromRqt(await file.text(), filename)
      : importGameCardFromJson(await file.text());
  await saveGameCard(card);
  return card;
}

export function validateGameCardImportFileSize(file: Blob): void {
  const filename = gameCardImportFilename(file);
  const { label, limit } = gameCardImportSizeLimit(filename);
  if (file.size <= limit) return;
  throw new Error(`${label} import is too large. Maximum size is ${formatImportSize(limit)}.`);
}

function gameCardImportFilename(file: Blob): string {
  return typeof File !== "undefined" && file instanceof File ? file.name : "import";
}

function gameCardImportSizeLimit(filename: string): { label: string; limit: number } {
  switch (gameCardImportKind(filename)) {
  case "rqp":
    return { label: ".rqp package", limit: GAME_CARD_IMPORT_SIZE_LIMIT_BYTES.rqp };
  case "rqt":
    return { label: ".rqt source", limit: GAME_CARD_IMPORT_SIZE_LIMIT_BYTES.rqt };
  case "rqg":
    return { label: ".rqg game document", limit: GAME_CARD_IMPORT_SIZE_LIMIT_BYTES.rqg };
  case "json":
    return { label: "JSON game card", limit: GAME_CARD_IMPORT_SIZE_LIMIT_BYTES.json };
  }
}

function gameCardImportKind(filename: string): GameCardImportKind {
  const lower = filename.toLowerCase();
  if (lower.endsWith(".rqt.txt")) return "rqt";
  if (lower.endsWith(".rqg.json")) return "rqg";
  if (lower.endsWith(".rqp.zip")) return "rqp";
  if (isUnsupportedRoQeetDoubleExtension(lower)) {
    throw new Error("Unsupported RoQeet game file extension. Use .rqt, .rqg, .rqp, or Safari aliases .rqt.txt, .rqg.json, .rqp.zip.");
  }
  if (lower.endsWith(".rqp") || lower.endsWith(".zip")) return "rqp";
  if (lower.endsWith(".rqt")) return "rqt";
  if (lower.endsWith(".rqg")) return "rqg";
  if (lower.endsWith(".json")) return "json";
  return "json";
}

function isUnsupportedRoQeetDoubleExtension(filename: string): boolean {
  return /\.(?:rqt|rqg|rqp)\./.test(filename);
}

function formatImportSize(bytes: number): string {
  return `${Math.round(bytes / MIB)} MB`;
}

export async function openOfficialRqtSample(sampleId: string): Promise<GameCard> {
  const sample = getOfficialRqtSample(sampleId);
  if (!sample) throw new Error(`Unknown official RQT sample: ${sampleId}`);

  const existing = (await storage.gameCards.list()).find(
    (card) =>
      card.extensions?.["sourceKind"] === "official-sample" &&
      card.extensions?.["officialRqtSampleId"] === sample.id,
  );
  if (existing) {
    if (shouldRefreshPristineOfficialRqtSample(existing, sample)) {
      const refreshed = officialRqtSampleCard(sample, existing);
      await saveGameCard(refreshed);
      return refreshed;
    }
    if (existing.enabled) return existing;
    const enabled = { ...existing, enabled: true };
    await saveGameCard(enabled);
    return enabled;
  }

  const tagged = officialRqtSampleCard(sample);
  await saveGameCard(tagged);
  return tagged;
}

function shouldRefreshPristineOfficialRqtSample(card: GameCard, sample: OfficialRqtSample): boolean {
  const extensions = card.extensions ?? {};
  return (
    extensions["sourceKind"] === "official-sample" &&
    extensions["officialRqtSampleId"] === sample.id &&
    extensions["sourceRqtStatus"] === "current" &&
    typeof extensions["sourceRqt"] === "string" &&
    extensions["sourceRqt"] !== sample.source
  );
}

function officialRqtSampleCard(sample: OfficialRqtSample, existing?: GameCard): GameCard {
  const card = importGameCardFromRqt(sample.source, sample.filename);
  const id = existing?.id ?? card.id;
  const definition = existing ? { ...card.definition, id } : card.definition;
  const tagged: GameCard = {
    ...card,
    ...(existing ? { id, sync: existing.sync } : {}),
    definition,
    enabled: true,
    extensions: {
      ...(card.extensions ?? {}),
      sourceKind: "official-sample",
      sourceRqtStatus: "current",
      officialRqtSampleId: sample.id,
      officialRqtSampleFilename: sample.filename,
      officialRqtSampleSourcePath: sample.sourcePath,
    },
  };
  return tagged;
}

export async function duplicateGameCard(card: GameCard, title: string): Promise<GameCard> {
  const definition = { ...card.definition, title };
  const extensions = duplicatedGameCardExtensions(card.extensions);
  const copy = createGameCard({
    definition,
    document: gameDefinitionToRoqeetJson(definition),
    enabled: false,
    ...(extensions ? { extensions } : {}),
  });
  await saveGameCard(copy);
  return copy;
}

function duplicatedGameCardExtensions(existing: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!existing) return undefined;
  const extensions: Record<string, unknown> = { ...existing };
  const officialSampleId = typeof extensions["officialRqtSampleId"] === "string"
    ? extensions["officialRqtSampleId"]
    : undefined;
  const basedOnSampleId = typeof extensions["basedOnOfficialRqtSampleId"] === "string"
    ? extensions["basedOnOfficialRqtSampleId"]
    : undefined;
  const sampleId = basedOnSampleId ?? officialSampleId;
  if (sampleId) {
    delete extensions["officialRqtSampleId"];
    delete extensions["officialRqtSampleFilename"];
    delete extensions["officialRqtSampleSourcePath"];
    extensions["sourceKind"] = "user-copy";
    extensions["basedOnOfficialRqtSampleId"] = sampleId;
  }
  if (typeof extensions["sourceRqt"] === "string") {
    extensions["sourceRqtStatus"] = "stale";
  }
  return Object.keys(extensions).length ? extensions : undefined;
}
